import { ApiOutlined, EditOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { AutoComplete, Button, ConfigProvider, Drawer, Empty, Flex, Input, Layout, Select, Spin, Table, Tag, Tooltip, notification } from "antd";
import type { TableProps } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { useCallback, useEffect, useState } from "react";
import { LoaderSpinner } from "../../components/common/Loader";
import { addFacility, getFacilities, getFacility, getFacilityLookups, searchBscaCbasFacilities, searchFacility, searchBscaUrgentCareFacilities, searchLacCbsaFacilities, updateFacility } from "../../service/httpsCalls";
import CreateFacility, { FacilityDetail } from "./createFacility";
import "./facility.scss";

const FacilityPaging = {
    DefaultPageIndex: 1,
    DefaultPageSize: 10
};

interface FacilitySearchItem {
    facilityId?: number;
    facilityName?: string;
    fullAddress?: string;
    city?: string;
    stateCode?: string;
    zipcode?: string;
    isFacilityOnGoogle?: boolean;
    businessHoursJson?: string;
    distanceMiles?: number;
    distanceKilometers?: number;
    location?: {
        latitude?: number;
        longitude?: number;
        coordinates?: number[];
    };
}

type FacilityStatus = boolean | undefined;

interface FacilitySelectOption {
    value: number;
    label: string;
}

interface FacilityApiSearchProps {
    title: string;
    description: string;
    placeholder: string;
    search: (data: any) => Promise<any>;
    includeRadius?: boolean;
    notify: (type: "success" | "error" | "warning" | "info", message: string) => void;
}

interface FacilityBusinessHoursRow {
    day: string;
    hours: string;
}

const defaultBusinessHourDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const getJsonValue = (source: any, paths: string[][]) => {
    for (const path of paths) {
        const value = path.reduce<any>((current, key) => current?.[key], source);
        if (value !== undefined && value !== null && value !== "") {
            return value;
        }
    }

    return undefined;
};

const normalizeBusinessHourTime = (value?: string | null) => {
    if (!value?.trim()) return "";

    return value
        .replace(/\u2013|\u2014/g, "-")
        .replace(/[^\x20-\x7E]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

const formatBusinessHourPeriodText = (period: any) => {
    const open = normalizeBusinessHourTime(period?.open ?? period?.Open);
    const close = normalizeBusinessHourTime(period?.close ?? period?.Close);

    if (open.toLowerCase() === "12:00 am" && close.toLowerCase() === "11:59 pm") {
        return "Open 24 hours";
    }

    if (!open && !close) {
        return "Hours not specified";
    }

    return `${open || "Open"} - ${close || "Close"}`;
};

const parseBusinessHoursRows = (businessHoursJson?: string): { rows: FacilityBusinessHoursRow[]; isValid: boolean } => {
    if (!businessHoursJson?.trim()) {
        return { rows: [], isValid: true };
    }

    try {
        const parsed = JSON.parse(businessHoursJson);
        const weekdayDescriptions = getJsonValue(parsed, [
            ["regularOpeningHours", "weekdayDescriptions"],
            ["regularOpeningHours", "weekdayText"],
            ["currentOpeningHours", "weekdayDescriptions"],
            ["opening_hours", "weekday_text"],
            ["RegularOpeningHours", "WeekdayDescriptions"]
        ]);

        if (Array.isArray(weekdayDescriptions) && weekdayDescriptions.length) {
            return {
                isValid: true,
                rows: weekdayDescriptions
                    .filter(item => typeof item === "string")
                    .map(description => {
                        const [day, ...hoursParts] = description.split(":");
                        return {
                            day: day?.trim() || "Day",
                            hours: normalizeBusinessHourTime(hoursParts.join(":")) || "Hours not specified"
                        };
                    })
            };
        }

        if (Array.isArray(parsed)) {
            return {
                isValid: true,
                rows: defaultBusinessHourDays.map(defaultDay => {
                    const savedDay = parsed.find(item => (item?.day ?? item?.Day) === defaultDay);
                    const closed = savedDay?.closed ?? savedDay?.Closed;
                    const periods = savedDay?.periods ?? savedDay?.Periods ?? [];

                    return {
                        day: defaultDay,
                        hours: closed
                            ? "Closed"
                            : Array.isArray(periods) && periods.length
                                ? periods.map(formatBusinessHourPeriodText).join(", ")
                                : "Hours not specified"
                    };
                })
            };
        }

        return { rows: [], isValid: true };
    } catch {
        return { rows: [], isValid: false };
    }
};

export default function Facilities() {
    const [api, contextHolder] = notification.useNotification();
    const [facilities, setFacilities] = useState<FacilityDetail[]>([]);
    const [searchText, setSearchText] = useState("");
    const [appliedSearchText, setAppliedSearchText] = useState("");
    const [selectedClientId, setSelectedClientId] = useState<number | undefined>();
    const [appliedClientId, setAppliedClientId] = useState<number | undefined>();
    const [clientOptions, setClientOptions] = useState<FacilitySelectOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [testDrawerOpen, setTestDrawerOpen] = useState(false);
    const [selectedFacility, setSelectedFacility] = useState<FacilityDetail | null>(null);
    const [currentPage, setCurrentPage] = useState(FacilityPaging.DefaultPageIndex);
    const [pageSize, setPageSize] = useState(FacilityPaging.DefaultPageSize);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortedInfo, setSortedInfo] = useState<SorterResult<FacilityDetail>>({});
    const [tableScrollY, setTableScrollY] = useState<number>();

    const notify = (type: "success" | "error" | "warning" | "info", message: string) => {
        api[type]({ message });
    };

    const getValue = <T,>(item: any, camelKey: string, pascalKey: string): T | undefined =>
        item?.[camelKey] ?? item?.[pascalKey];

    const mapClientOptions = (items?: any[]): FacilitySelectOption[] => (items ?? [])
        .map(item => {
            const value = Number(
                getValue<any>(item, "value", "Value")
                ?? getValue<any>(item, "clientId", "ClientId")
                ?? getValue<any>(item, "id", "Id")
            );
            const label =
                getValue<string>(item, "name", "Name")?.trim()
                ?? getValue<string>(item, "companyName", "CompanyName")?.trim()
                ?? getValue<string>(item, "clientName", "ClientName")?.trim()
                ?? "";

            return { value, label };
        })
        .filter(option => option.value > 0 && option.label);

    const parseTreatmentValues = (value?: string) => {
        if (!value) return [];

        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return parseCommaSeparatedValues(value);
        }
    };

    const parseCommaSeparatedValues = (value?: string) => {
        if (!value) return [];

        const values: string[] = [];
        let currentValue = "";
        let parenthesisDepth = 0;

        for (const character of value) {
            if (character === "(") {
                parenthesisDepth += 1;
            } else if (character === ")" && parenthesisDepth > 0) {
                parenthesisDepth -= 1;
            }

            if (character === "," && parenthesisDepth === 0) {
                const trimmedValue = currentValue.trim();
                if (trimmedValue) values.push(trimmedValue);
                currentValue = "";
                continue;
            }

            currentValue += character;
        }

        const trimmedValue = currentValue.trim();
        if (trimmedValue) values.push(trimmedValue);

        return values;
    };

    const normalizeStringList = (value: any) =>
        Array.isArray(value) ? value : parseCommaSeparatedValues(value?.toString());

    const normalizeBoolean = (value: any) => {
        if (value === true || value === 1 || value === "1") return true;
        if (value === false || value === 0 || value === "0") return false;

        const normalizedValue = value?.toString?.().trim().toLowerCase();
        if (["true", "active", "yes"].includes(normalizedValue)) return true;
        if (["false", "inactive", "no"].includes(normalizedValue)) return false;

        return false;
    };

    const normalizeFacilityStatus = (value: any): FacilityStatus => {
        if (value === undefined || value === null || value === "") return undefined;
        if (value === true || value === 1 || value === "1") return true;
        if (value === false || value === 0 || value === "0") return false;

        const normalizedValue = value?.toString?.().trim().toLowerCase();
        if (["true", "active", "yes"].includes(normalizedValue)) return true;
        if (["false", "inactive", "no"].includes(normalizedValue)) return false;

        return undefined;
    };

    const normalizeFacility = (item: any): FacilityDetail => {
        const addressLine1 = getValue<string>(item, "addressLine1", "AddressLine1") ?? "";
        const addressLine2 = getValue<string>(item, "addressLine2", "AddressLine2");
        const treatmentTypes = getValue<string[]>(item, "treatmentTypes", "TreatmentTypes")
            ?? getValue<string[]>(item, "treatmentTypeIds", "TreatmentTypeIds")
            ?? parseTreatmentValues(
                getValue<string>(item, "treatmentTypesText", "TreatmentTypesText")
                ?? getValue<string>(item, "treatmentTypes", "TreatmentTypes")
                ?? getValue<string>(item, "treatmentTypeIdsJson", "TreatmentTypeIdsJson")
            );
        const tags = normalizeStringList(
            getValue<any>(item, "tags", "Tags")
            ?? getValue<any>(item, "tagsText", "TagsText")
            ?? getValue<any>(item, "tag", "Tag")
        );
        const facilityServiceType = normalizeStringList(
            getValue<any>(item, "facilityServiceType", "FacilityServiceType")
            ?? getValue<any>(item, "facilityServiceTypes", "FacilityServiceTypes")
        );
        const pickupPoints = getValue<any[]>(item, "pickupPoints", "PickupPoints") ?? [];
        const clientTreatmentTypes = getValue<any[]>(item, "clientTreatmentTypes", "ClientTreatmentTypes") ?? [];

        return {
            id: getValue<number>(item, "id", "Id") ?? 0,
            facilityName: getValue<string>(item, "facilityName", "FacilityName") ?? "",
            facilityServiceType,
            isActive: normalizeFacilityStatus(
                getValue<any>(item, "isActive", "IsActive")
                ?? getValue<any>(item, "facilityStatus", "FacilityStatus")
                ?? getValue<any>(item, "status", "Status")
            ),
            isFacilityOnGoogle: normalizeBoolean(getValue<any>(item, "isFacilityOnGoogle", "IsFacilityOnGoogle")),
            addressLine1,
            addressLine2,
            address: [addressLine1, addressLine2].filter(Boolean).join(", "),
            city: getValue<string>(item, "city", "City") ?? "",
            stateCode: getValue<string>(item, "stateCode", "StateCode"),
            zipcode: getValue<string>(item, "zipcode", "Zipcode") ?? "",
            county: getValue<string>(item, "county", "County"),
            countryCode: getValue<string>(item, "countryCode", "CountryCode") ?? "US",
            mailingAddressLine1: getValue<string>(item, "mailingAddressLine1", "MailingAddressLine1"),
            mailingAddressLine2: getValue<string>(item, "mailingAddressLine2", "MailingAddressLine2"),
            mailingCity: getValue<string>(item, "mailingCity", "MailingCity"),
            mailingStateCode: getValue<string>(item, "mailingStateCode", "MailingStateCode"),
            mailingZipcode: getValue<string>(item, "mailingZipcode", "MailingZipcode"),
            mailingCounty: getValue<string>(item, "mailingCounty", "MailingCounty"),
            mailingCountryCode: getValue<string>(item, "mailingCountryCode", "MailingCountryCode") ?? "US",
            isSameAsPhysicalAddress: normalizeBoolean(getValue<any>(item, "isSameAsPhysicalAddress", "IsSameAsPhysicalAddress")),
            latitude: getValue<number>(item, "latitude", "Latitude"),
            longitude: getValue<number>(item, "longitude", "Longitude"),
            placeId: getValue<string>(item, "placeId", "PlaceId"),
            mainPhone: getValue<string>(item, "mainPhone", "MainPhone"),
            alternatePhone: getValue<string>(item, "alternatePhone", "AlternatePhone"),
            fax: getValue<string>(item, "fax", "Fax"),
            email: getValue<string>(item, "email", "Email"),
            timeZoneId: getValue<string>(item, "timeZoneId", "TimeZoneId"),
            businessHoursJson: getValue<string>(item, "businessHoursJson", "BusinessHoursJson"),
            addressDataJson: getValue<string>(item, "addressDataJson", "AddressDataJson"),
            geocodeResponseJson: getValue<string>(item, "geocodeResponseJson", "GeocodeResponseJson"),
            placeDetailsResponseJson: getValue<string>(item, "placeDetailsResponseJson", "PlaceDetailsResponseJson"),
            destinationAPiResponseJson: getValue<string>(item, "destinationAPiResponseJson", "DestinationAPiResponseJson"),
            treatmentTypes,
            notes: getValue<string>(item, "notes", "Notes"),
            tags,
            pickupPoints: pickupPoints.map(point => ({
                id: getValue<number>(point, "id", "Id") ?? Date.now(),
                pickupPointCode: getValue<string>(point, "pickupPointCode", "PickupPointCode") ?? "",
                pickupPointName: getValue<string>(point, "pickupPointName", "PickupPointName") ?? "",
                pointType: getValue<string>(point, "pointType", "PointType") ?? "",
                sectionName: getValue<string>(point, "sectionName", "SectionName"),
                floorLevel: getValue<string>(point, "floorLevel", "FloorLevel"),
                landmark: getValue<string>(point, "landmark", "Landmark"),
                instructions: getValue<string>(point, "instructions", "Instructions"),
                latitude: getValue<number>(point, "latitude", "Latitude") ?? 0,
                longitude: getValue<number>(point, "longitude", "Longitude") ?? 0,
                isDefault: normalizeBoolean(getValue<any>(point, "isDefault", "IsDefault")),
                notes: getValue<string>(point, "notes", "Notes")
            })),
            clientTreatmentTypes: clientTreatmentTypes.map(row => ({
                id: getValue<number>(row, "id", "Id"),
                clientId: Number(getValue<any>(row, "clientId", "ClientId") ?? 0),
                treatmentTypes: normalizeStringList(
                    getValue<any>(row, "treatmentTypes", "TreatmentTypes")
                    ?? getValue<any>(row, "treatmentTypeName", "TreatmentTypeName")
                ),
                actions: normalizeStringList(
                    getValue<any>(row, "actions", "Actions")
                    ?? getValue<any>(row, "action", "Action")
                )
            })).filter(row => row.clientId > 0)
        };
    };

    const loadFacilities = useCallback(async () => {
        setLoading(true);
        try {
            const sortField = Array.isArray(sortedInfo.field) ? sortedInfo.field[0]?.toString() : sortedInfo.field?.toString();
            const response = await getFacilities({
                searchText: appliedSearchText.trim() || null,
                clientId: appliedClientId ?? null,
                pageIndex: currentPage,
                pageSize,
                sortField,
                sortDescending: sortedInfo.order === "descend"
            });
            const items = response?.items ?? response?.Items ?? [];
            setFacilities(items.map(normalizeFacility));
            setTotalRecords(response?.totalRecords ?? response?.TotalRecords ?? 0);
        } catch {
            notify("error", "Unable to load facilities.");
        } finally {
            setLoading(false);
        }
    }, [appliedClientId, appliedSearchText, currentPage, pageSize, sortedInfo.field, sortedInfo.order]);

    const openAddFacility = () => {
        setSelectedFacility(null);
        setDrawerOpen(true);
    };

    const openTestDrawer = () => {
        setTestDrawerOpen(true);
    };

    const openEditFacility = async (facility: FacilityDetail) => {
        setLoading(true);
        try {
            const details = await getFacility(facility.id);
            setSelectedFacility(details ? normalizeFacility(details) : facility);
            setDrawerOpen(true);
        } catch {
            notify("error", "Unable to load facility details.");
        } finally {
            setLoading(false);
        }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedFacility(null);
    };

    const buildAddress = (facility: FacilityDetail) => [
        facility.addressLine1,
        facility.addressLine2
    ].filter(Boolean).join(", ");

    const getFacilitySaveErrorMessage = (error: any, fallbackMessage: string) => {
        const responseData = error?.response?.data;
        const serverMessage =
            responseData?.messages?.[0]?.description
            ?? responseData?.Messages?.[0]?.Description
            ?? responseData?.messages?.[0]?.message
            ?? responseData?.Messages?.[0]?.Message
            ?? responseData?.title
            ?? responseData?.Title
            ?? responseData?.message
            ?? responseData?.Message
            ?? (typeof responseData === "string" ? responseData : "")
            ?? error?.message
            ?? "";

        return serverMessage || fallbackMessage;
    };

    const handleFacilitySaved = async (facility: FacilityDetail) => {
        setSaving(true);
        try {
            if (selectedFacility) {
                await updateFacility(selectedFacility.id, facility);
            } else {
                const { id, ...addPayload } = facility;
                await addFacility(addPayload);
            }
            notify("success", selectedFacility ? "Facility updated successfully." : "Facility added successfully.");
            closeDrawer();
            await loadFacilities();
        } catch (error) {
            const fallbackMessage = selectedFacility ? "Unable to update facility." : "Unable to add facility.";
            notify("error", getFacilitySaveErrorMessage(error, fallbackMessage));
        } finally {
            setSaving(false);
        }
    };

    const renderEllipsisCell = (value?: string) => value ? (
        <Tooltip title={value} placement="topLeft">
            <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {value}
            </span>
        </Tooltip>
    ) : "";

    const columns: TableProps<FacilityDetail>["columns"] = [
        {
            title: "Facility Name",
            dataIndex: "facilityName",
            width: 230,
            sorter: true,
            sortOrder: sortedInfo.field === "facilityName" ? sortedInfo.order : null,
            ellipsis: { showTitle: false },
            render: renderEllipsisCell
        },
        {
            title: "Address",
            width: 200,
            ellipsis: { showTitle: false },
            render: (_, record) => renderEllipsisCell(buildAddress(record))
        },
        {
            title: "City",
            dataIndex: "city",
            width: 140,
            sorter: true,
            sortOrder: sortedInfo.field === "city" ? sortedInfo.order : null,
            ellipsis: true
        },
        {
            title: "Facility Status",
            dataIndex: "isActive",
            width: 130,
            sorter: true,
            sortOrder: sortedInfo.field === "isActive" ? sortedInfo.order : null,
            render: value => value === true
                ? <Tag color="green">Active</Tag>
                : value === false
                    ? <Tag color="red">Inactive</Tag>
                    : <Tag>Unknown</Tag>
        },
        {
            title: "State",
            dataIndex: "stateCode",
            width: 90
        },
        {
            title: "Zipcode",
            dataIndex: "zipcode",
            width: 110
        },
        {
            title: "County",
            dataIndex: "county",
            width: 140,
            ellipsis: true
        },
        {
            title: "Main Phone",
            dataIndex: "mainPhone",
            width: 150
        },
        {
            title: "OnGoogle",
            dataIndex: "isFacilityOnGoogle",
            width: 110,
            sorter: true,
            sortOrder: sortedInfo.field === "isFacilityOnGoogle" ? sortedInfo.order : null,
            render: value => value ? "Yes" : "No"
        },
        {
            title: "Tags",
            width: 150,
            ellipsis: true,
            render: (_, record) => renderEllipsisCell(record.tags?.join(", ") ?? "")
        },
        {
            title: "Action",
            width: 90,
            fixed: "right",
            render: (_, record) => (
                <Tooltip title="Edit Facility">
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        style={{ backgroundColor: "#fff", borderColor: "#d9d9d9", color: "rgba(0, 0, 0, 0.88)" }}
                        onClick={() => openEditFacility(record)}
                    />
                </Tooltip>
            )
        }
    ];

    const runSearch = () => {
        setAppliedSearchText(searchText);
        setAppliedClientId(selectedClientId);
        setCurrentPage(FacilityPaging.DefaultPageIndex);
    };

    const resetFilters = () => {
        setSearchText("");
        setAppliedSearchText("");
        setSelectedClientId(undefined);
        setAppliedClientId(undefined);
        setCurrentPage(FacilityPaging.DefaultPageIndex);
    };

    const handleTableChange: TableProps<FacilityDetail>["onChange"] = (pagination, _filters, sorter) => {
        setCurrentPage(pagination.current ?? FacilityPaging.DefaultPageIndex);
        setPageSize(pagination.pageSize ?? FacilityPaging.DefaultPageSize);
        setSortedInfo(sorter as SorterResult<FacilityDetail>);
    };

    useEffect(() => {
        document.title = "Facility";

        const computeTableHeight = () => {
            const height = window.innerHeight - 355;
            setTableScrollY(height > 0 ? height : undefined);
        };

        computeTableHeight();
        window.addEventListener("resize", computeTableHeight);
        return () => window.removeEventListener("resize", computeTableHeight);
    }, []);

    useEffect(() => {
        let isMounted = true;

        setLookupLoading(true);
        getFacilityLookups()
            .then(response => {
                if (!isMounted || !response) return;
                setClientOptions(mapClientOptions(response.clientList ?? response.ClientList));
            })
            .catch(() => {
                if (isMounted) {
                    notify("warning", "Unable to load client filter values.");
                }
            })
            .finally(() => {
                if (isMounted) {
                    setLookupLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        loadFacilities();
    }, [loadFacilities]);

    return (
        <ConfigProvider theme={{ components: { Layout: { bodyBg: "#eff1f5", siderBg: "#fff" } } }}>
            {contextHolder}
            <Layout className="facility-list-page" style={{ padding: "20px", minWidth: 0 }}>
                <Drawer
                    title={selectedFacility ? `Edit Facility: ${selectedFacility.facilityName}` : "Add Facility"}
                    destroyOnClose
                    maskClosable={false}
                    open={drawerOpen}
                    width={window.innerWidth < 768 ? "100%" : Math.max(window.innerWidth * 0.8, 1000)}
                    onClose={closeDrawer}
                    styles={{ body: { background: "#eff1f5", overflow: "hidden" } }}
                >
                    <CreateFacility
                        facility={selectedFacility}
                        onCancel={closeDrawer}
                        onSaved={handleFacilitySaved}
                        notify={notify}
                        saving={saving}
                    />
                </Drawer>
                <Drawer
                    title="Facility Search API Test"
                    destroyOnClose
                    open={testDrawerOpen}
                    width={window.innerWidth < 768 ? "100%" : 620}
                    onClose={() => setTestDrawerOpen(false)}
                    styles={{ body: { background: "#eff1f5" } }}
                >
                    <div className="facility-api-test-drawer">
                        <FacilityApiSearch
                            title="Search Facility"
                            description="Search the facility rolodex index."
                            placeholder="Type facility name, address, city, state, zip, or place id"
                            search={searchFacility}
                            notify={notify}
                        />
                        <FacilityApiSearch
                            title="Search BSCA / CBAS Facilities"
                            description="Search the BSCA / CBAS facility index."
                            placeholder="Type BSCA / CBAS facility name or address"
                            search={searchBscaCbasFacilities}
                            includeRadius
                            notify={notify}
                        />
                        <FacilityApiSearch
                            title="Search LAC / CBSA Facilities"
                            description="Search the LAC / CBSA facility index."
                            placeholder="Type LAC / CBSA facility name or address"
                            search={searchLacCbsaFacilities}
                            includeRadius
                            notify={notify}
                        />
                        <FacilityApiSearch
                            title="Search BSCA Urgent Care Facility"
                            description="Search the BSCA Urgent Care facility rolodex index."
                            placeholder="Type BSCA Urgent Care facility name or address"
                            search={searchBscaUrgentCareFacilities}
                            notify={notify}
                        />
                    </div>
                </Drawer>
                <h3 style={{ textTransform: "uppercase", marginTop: 0 }}>Facility</h3>
                <div style={{ background: "#fff", padding: "1rem", marginBottom: "2rem", minWidth: 0 }}>
                    <Flex vertical gap="small">
                        <Flex gap="small" align="center" justify="space-between" wrap="wrap">
                            <Flex gap="small" align="center" wrap="wrap">
                                <Input
                                    value={searchText}
                                    placeholder="Facility name / address / city / state / zip / place id / tags"
                                    allowClear
                                    style={{ flex: "0 1 432px", width: 432, minWidth: 260, maxWidth: "100%" }}
                                    onChange={event => setSearchText(event.target.value)}
                                    onPressEnter={runSearch}
                                />
                                <Button onClick={runSearch} loading={loading} type="primary">Search</Button>
                                <Button onClick={resetFilters}>Reset</Button>
                            </Flex>
                            <Flex gap="small" wrap="wrap" justify="flex-end">
                                <Button icon={<ApiOutlined />} onClick={openTestDrawer}>
                                    Test
                                </Button>
                                <Button type="primary" icon={<PlusOutlined />} onClick={openAddFacility}>
                                    Add Facility
                                </Button>
                            </Flex>
                        </Flex>
                        <Flex gap="middle" align="center" wrap="wrap">
                            <Select
                                value={selectedClientId}
                                allowClear
                                showSearch
                                loading={lookupLoading}
                                placeholder="Client"
                                optionFilterProp="label"
                                options={clientOptions}
                                style={{ flex: "0 1 432px", width: 432, minWidth: 260, maxWidth: "100%" }}
                                onChange={value => setSelectedClientId(value)}
                                onKeyDown={event => {
                                    if (event.key === "Enter") runSearch();
                                }}
                            />
                        </Flex>
                    </Flex>
                </div>
                <div className="facility-list-table">
                    <Table<FacilityDetail>
                        style={{ background: "#fff", width: "100%", maxWidth: "100%" }}
                        tableLayout="fixed"
                        scroll={{ x: 1480, y: facilities.length > 0 ? tableScrollY : undefined }}
                        dataSource={facilities}
                        columns={columns}
                        rowKey="id"
                        size="small"
                        loading={{ indicator: <LoaderSpinner size={24} topMargin="10px" />, spinning: loading }}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            total: totalRecords,
                            showTotal: (total, range) => `${range[0] || 0}-${range[1] || 0} of ${total} items`,
                            showSizeChanger: true
                        }}
                        onChange={handleTableChange}
                    />
                </div>
            </Layout>
        </ConfigProvider>
    );
}

function FacilityApiSearch({ title, description, placeholder, search, includeRadius = false, notify }: FacilityApiSearchProps) {
    const [query, setQuery] = useState("");
    const [latitude, setLatitude] = useState("");
    const [longitude, setLongitude] = useState("");
    const [withinMiles, setWithinMiles] = useState("");
    const [items, setItems] = useState<FacilitySearchItem[]>([]);
    const [selectedItem, setSelectedItem] = useState<FacilitySearchItem | null>(null);
    const [loading, setLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const getValue = <T,>(item: any, camelKey: string, pascalKey: string): T | undefined =>
        item?.[camelKey] ?? item?.[pascalKey];

    const normalizeSearchItem = (item: any): FacilitySearchItem => ({
        facilityId: getValue<number>(item, "facilityId", "FacilityId"),
        facilityName: getValue<string>(item, "facilityName", "FacilityName"),
        fullAddress: getValue<string>(item, "fullAddress", "FullAddress"),
        city: getValue<string>(item, "city", "City"),
        stateCode: getValue<string>(item, "stateCode", "StateCode"),
        zipcode: getValue<string>(item, "zipcode", "Zipcode"),
        isFacilityOnGoogle: getValue<boolean>(item, "isFacilityOnGoogle", "IsFacilityOnGoogle"),
        businessHoursJson: getValue<string>(item, "businessHoursJson", "BusinessHoursJson"),
        distanceMiles: getValue<number>(item, "distanceMiles", "DistanceMiles"),
        distanceKilometers: getValue<number>(item, "distanceKilometers", "DistanceKilometers"),
        location: getValue<any>(item, "location", "Location")
    });

    const buildPayload = (searchText: string) => {
        const payload: any = { searchText: searchText.trim() };
        const lat = Number(latitude);
        const lng = Number(longitude);
        const miles = Number(withinMiles);

        if (!Number.isNaN(lat) && latitude.trim()) payload.latitude = lat;
        if (!Number.isNaN(lng) && longitude.trim()) payload.longitude = lng;
        if (includeRadius && !Number.isNaN(miles) && withinMiles.trim()) payload.withinMiles = miles;

        return payload;
    };

    const getCoordinateValidationMessage = () => {
        const hasLatitude = latitude.trim().length > 0;
        const hasLongitude = longitude.trim().length > 0;
        const hasWithinMiles = includeRadius && withinMiles.trim().length > 0;
        const lat = Number(latitude);
        const lng = Number(longitude);
        const miles = Number(withinMiles);

        if (hasLatitude !== hasLongitude) {
            return "Both latitude and longitude are required when searching by location.";
        }

        if (hasLatitude && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
            return "Latitude must be a number between -90 and 90.";
        }

        if (hasLongitude && (Number.isNaN(lng) || lng < -180 || lng > 180)) {
            return "Longitude must be a number between -180 and 180.";
        }

        if (hasLatitude && hasLongitude && (lat === 0 || lng === 0)) {
            return "Latitude and longitude must be non-zero values.";
        }

        if (hasWithinMiles && (Number.isNaN(miles) || miles < 0)) {
            return "Within miles must be 0 or greater.";
        }

        if (hasWithinMiles && miles > 0 && (!hasLatitude || !hasLongitude)) {
            return "Latitude and longitude are required when within miles is greater than 0.";
        }

        return "";
    };

    const runSearch = useCallback(async (value: string, showValidation = false) => {
        const nextQuery = value.trim();
        const validationMessage = getCoordinateValidationMessage();
        const canSearchByText = nextQuery.length >= 2;
        const canSearchByCoordinates = latitude.trim().length > 0 && longitude.trim().length > 0 && !validationMessage;

        if (!canSearchByText && !canSearchByCoordinates) {
            setItems([]);
            setDropdownOpen(false);
            setLoading(false);
            if (showValidation) {
                notify("warning", validationMessage || "Enter at least 2 characters or both latitude and longitude.");
            }
            return;
        }

        if (validationMessage) {
            if (showValidation) notify("warning", validationMessage);
            setDropdownOpen(false);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            const response = await search(buildPayload(nextQuery));
            const responseItems = response?.items ?? response?.Items ?? [];
            setItems(responseItems.map(normalizeSearchItem));
            setDropdownOpen(responseItems.length > 0);
        } catch {
            setItems([]);
            setDropdownOpen(false);
            notify("error", `Unable to run ${title}.`);
        } finally {
            setLoading(false);
        }
    }, [latitude, longitude, withinMiles, includeRadius, search, title]);

    useEffect(() => {
        const debounce = window.setTimeout(() => {
            runSearch(query);
        }, 350);

        return () => window.clearTimeout(debounce);
    }, [query, runSearch]);

    const buildAddress = (item: FacilitySearchItem) => [
        item.fullAddress,
        [item.city, item.stateCode, item.zipcode].filter(Boolean).join(", ")
    ].filter(Boolean).join(" - ");

    const options = items.map((item, index) => ({
        value: `${item.facilityName ?? "Facility"}-${item.facilityId ?? index}`,
        item,
        label: (
            <div className="facility-api-option">
                <strong>{item.facilityName || "Unnamed facility"}</strong>
                <span>{buildAddress(item) || "Address not available"}</span>
            </div>
        )
    }));

    const handleSelect = (_value: string, option: any) => {
        const item = option.item as FacilitySearchItem;
        setSelectedItem(item);
        setQuery(item.facilityName ?? "");
        setDropdownOpen(false);
    };

    return (
        <section className="facility-api-test-section">
            <div className="facility-api-test-header">
                <div>
                    <h4>{title}</h4>
                    <p>{description}</p>
                </div>
                <Tag color="blue">{items.length} results</Tag>
            </div>

            <AutoComplete
                value={query}
                options={options}
                open={dropdownOpen}
                onChange={value => {
                    setQuery(value);
                    setSelectedItem(null);
                    setDropdownOpen(false);
                }}
                onDropdownVisibleChange={open => setDropdownOpen(open && options.length > 0)}
                onSelect={handleSelect}
                notFoundContent={loading ? <Spin size="small" /> : query.trim().length >= 2 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No facilities found" /> : null}
                popupMatchSelectWidth
                style={{ width: "100%" }}
            >
                <Input allowClear prefix={<SearchOutlined />} placeholder={placeholder} />
            </AutoComplete>

            <Flex gap="small" wrap="wrap" className="facility-api-coordinate-row">
                <Input value={latitude} placeholder="Latitude" onChange={event => setLatitude(event.target.value)} />
                <Input value={longitude} placeholder="Longitude" onChange={event => setLongitude(event.target.value)} />
                {includeRadius && (
                    <Input value={withinMiles} placeholder="Within miles" onChange={event => setWithinMiles(event.target.value)} />
                )}
                <Button type="primary" loading={loading} onClick={() => runSearch(query, true)}>
                    Search
                </Button>
            </Flex>

            <FacilityApiResult item={selectedItem} />
        </section>
    );
}

function FacilityApiResult({ item }: { item: FacilitySearchItem | null }) {
    if (!item) {
        return (
            <div className="facility-api-selected-empty">
                Select a suggestion to view the API response.
            </div>
        );
    }

    const rows = [
        ["Facility ID", item.facilityId],
        ["Facility Name", item.facilityName],
        ["Address", item.fullAddress],
        ["City", item.city],
        ["State", item.stateCode],
        ["Zipcode", item.zipcode],
        ["On Google", item.isFacilityOnGoogle === undefined ? undefined : item.isFacilityOnGoogle ? "Yes" : "No"],
        ["Distance", item.distanceMiles !== undefined ? `${item.distanceMiles.toFixed(2)} mi` : undefined],
        ["Latitude", item.location?.latitude],
        ["Longitude", item.location?.longitude]
    ];

    return (
        <div className="facility-api-selected-card">
            <div className="facility-api-selected-title">Selected Response</div>
            <div className="facility-api-selected-grid">
                {rows.map(([label, value]) => (
                    <div className="facility-api-selected-field" key={label?.toString()}>
                        <span>{label}</span>
                        <strong>{value === undefined || value === null || value === "" ? "-" : value}</strong>
                    </div>
                ))}
            </div>
            {item.businessHoursJson && (
                <BusinessHoursResult businessHoursJson={item.businessHoursJson} />
            )}
        </div>
    );
}

function BusinessHoursResult({ businessHoursJson }: { businessHoursJson: string }) {
    const { rows, isValid } = parseBusinessHoursRows(businessHoursJson);

    if (rows.length) {
        return (
            <div className="facility-json-hours facility-api-business-hours">
                <div className="facility-json-section-title">Business Hours</div>
                <div className="facility-json-hours-list">
                    {rows.map(row => (
                        <div className="facility-json-hours-row" key={row.day}>
                            <span>{row.day}</span>
                            <strong>{row.hours}</strong>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="facility-api-json-block">
            <span>Business Hours</span>
            <pre>{isValid ? "Business hours are not available in a displayable format." : businessHoursJson}</pre>
        </div>
    );
}
